import { Link } from "react-router-dom";
import { Activity, Trophy, Flame, CalendarClock, History, BarChart3 } from "lucide-react";
import { AccountBox } from "./Account";

const STATUS_ITEMS = [
  { id: null, label: "Tutte", icon: BarChart3 },
  { id: "live", label: "Live", icon: Flame },
  { id: "upcoming", label: "In Programma", icon: CalendarClock },
  { id: "finished", label: "Finite", icon: History },
];

function FilterButton({ active, onClick, testId, children, accent = "#39FF14" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-3 px-3 py-2.5 text-left text-sm font-medium transition-colors border-l-2 ${
        active ? "bg-[#1A1A1A] text-white" : "text-[#A1A1AA] hover:bg-[#141414] hover:text-white border-transparent"
      }`}
      style={active ? { borderColor: accent } : undefined}
    >
      {children}
    </button>
  );
}

export default function Sidebar({ leagues = [], activeLeague, onLeague, activeStatus, onStatus, counts = {} }) {
  return (
    <aside
      data-testid="sidebar"
      className="w-full lg:w-72 shrink-0 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto border-b lg:border-b-0 lg:border-r border-[#27272A] bg-black/40 backdrop-blur-md"
    >
      <div className="p-6 lg:p-8">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#39FF14] text-black grid place-items-center" style={{ clipPath: "polygon(0 0, 100% 0, 85% 100%, 0 100%)" }}>
            <Activity strokeWidth={2.5} size={20} />
          </div>
          <div>
            <div className="heading text-xl font-black leading-none">GOAL.AI</div>
            <div className="label mt-1 text-[10px]">Pronostici Precisi</div>
          </div>
        </Link>

        <div className="mt-8"><AccountBox /></div>

        <div className="mt-8">
          <div className="label mb-3">Stato Partita</div>
          <div className="flex flex-col gap-1">
            {STATUS_ITEMS.map(({ id, label, icon: Icon }) => (
              <FilterButton key={label} active={(activeStatus ?? null) === id} onClick={() => onStatus(id)} testId={`filter-status-${id ?? "all"}`}>
                <Icon size={16} strokeWidth={1.5} />
                <span>{label}</span>
                {id === "live" && counts.live > 0 && (
                  <span className="ml-auto flex items-center gap-1.5 text-[10px] font-bold text-[#FF3B30]">
                    <span className="live-dot" /> {counts.live}
                  </span>
                )}
              </FilterButton>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="label mb-3 flex items-center gap-2">
            <Trophy size={12} strokeWidth={1.5} /> Campionati
          </div>
          <div className="flex flex-col gap-1">
            <FilterButton active={!activeLeague} onClick={() => onLeague(null)} testId="filter-league-all" accent="#007AFF">
              <span>Tutti i Campionati</span>
              <span className="ml-auto mono text-[10px] text-[#71717A]">ALL</span>
            </FilterButton>
            {leagues.map((l) => (
              <FilterButton key={l.id} active={activeLeague === l.id} onClick={() => onLeague(l.id)} testId={`filter-league-${l.id}`} accent="#007AFF">
                <span className="truncate">{l.name}</span>
                <span className="ml-auto flex items-center gap-2">
                  {counts.byLeague?.[l.id] > 0 && <span className="mono text-[10px] text-[#39FF14]">{counts.byLeague[l.id]}</span>}
                  <span className="mono text-[10px] text-[#71717A]">{l.code}</span>
                </span>
              </FilterButton>
            ))}
          </div>
        </div>

        <div className="mt-10 p-4 border border-[#27272A] bg-black/40">
          <div className="label text-[10px] mb-2">Fonti dati</div>
          <div className="text-xs text-[#A1A1AA] leading-relaxed">
            Calendario, live, formazioni e quote: ESPN · Meteo: Open-Meteo · Analisi: GPT-6 Sol
          </div>
        </div>
      </div>
    </aside>
  );
}
